import {
    Box,
    Button,
    Card,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import type { ProductMinimal } from '@travelity/api';
import UnitProductsTable from '../UnitProductsTable';
import { buildDayOptions } from '../../helpers';

type UnitDayCardProps = {
    dayIndex: number;
    day: {
        day_index: number;
        items: Array<ProductMinimal & { start_time?: string }>;
    };
    prevDayIndex?: number;
    showErrors: boolean;
    dayIndexError?: string;
    onChangeDayIndex: (dayIndex: number, nextValue: number) => void;
    onOpenAddProduct: (dayIndex: number) => void;
    onRemoveDay: (dayIndex: number) => void;
    onRemoveProduct: (dayIndex: number, productId: string) => void;
};

const UnitDayCard = ({
    dayIndex,
    day,
    prevDayIndex,
    showErrors,
    dayIndexError,
    onChangeDayIndex,
    onOpenAddProduct,
    onRemoveDay,
    onRemoveProduct,
}: UnitDayCardProps) => {
    const minSelectableDay =
        dayIndex === 0 ? 1 : (prevDayIndex ?? dayIndex) + 1;
    const dayOptions = buildDayOptions(minSelectableDay);

    return (
        <Card
            variant="outlined"
            sx={{
                backgroundColor: 'background.paper',
                p: 3,
            }}
        >
            <Stack spacing={2}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                >
                    <Box sx={{ maxWidth: 240 }}>
                        <Typography variant="caption" color="text.secondary">
                            Product day
                        </Typography>
                        <Select
                            value={day.day_index}
                            onChange={event =>
                                onChangeDayIndex(
                                    dayIndex,
                                    Number(event.target.value)
                                )
                            }
                            size="small"
                            disabled={dayIndex === 0}
                            sx={{ width: 1, mt: 0.5 }}
                        >
                            {dayOptions.map(option => (
                                <MenuItem key={option} value={option}>
                                    Day {option}
                                </MenuItem>
                            ))}
                        </Select>
                        {showErrors && dayIndexError && (
                            <Typography variant="caption" color="error">
                                {dayIndexError}
                            </Typography>
                        )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Button
                            variant="outlined"
                            onClick={() => onOpenAddProduct(dayIndex)}
                            sx={{ height: 'fit-content' }}
                        >
                            Add product
                        </Button>
                        {dayIndex > 0 && (
                            <Button
                                color="secondary"
                                variant="contained"
                                onClick={() => onRemoveDay(dayIndex)}
                                sx={{ width: 'fit-content' }}
                            >
                                Remove day
                            </Button>
                        )}
                    </Box>
                </Stack>
                {day.items?.length > 0 && (
                    <UnitProductsTable
                        dayIndex={dayIndex}
                        products={day.items || []}
                        onRemove={productId =>
                            onRemoveProduct(dayIndex, productId)
                        }
                    />
                )}
            </Stack>
        </Card>
    );
};

export default UnitDayCard;
